import React from 'react'
import BackButton from '../ui/BackButton'

type BackTarget =
  | { to: string; label?: string }
  | { onClick: () => void; label?: string }

export interface PageHeaderProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
  actions?: React.ReactNode
  backTo?: BackTarget
  className?: string
}

/**
 * Unified page header used across every protected page that hides the global
 * TopBar (project create/edit, /projects/:id/next/*, /projects/:id/mechanical-tests/*,
 * etc.). Mirrors the canonical TopBar look — sticky 60 px bar in blueprint-blue —
 * so headers stay the same height on every screen.
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  backTo,
  className
}: PageHeaderProps) {
  const back = backTo ? (
    'to' in backTo ? (
      <BackButton to={backTo.to} tooltip={backTo.label ?? 'Back'} />
    ) : (
      <BackButton onClick={backTo.onClick} tooltip={backTo.label ?? 'Back'} />
    )
  ) : null

  return (
    <header
      className={[
        'sticky top-0 z-30 -mx-6 -mt-6 mb-6 flex min-h-[60px] w-auto min-w-0 flex-wrap items-center gap-x-4 gap-y-2 py-2',
        'border-b border-[#E2E5EA] bg-white/95 px-6 backdrop-blur-xl',
        className ?? ''
      ].join(' ')}
    >
      {back}
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <div className="flex min-w-0 items-center gap-2 truncate font-heading text-[16px] font-semibold leading-tight text-blueprint-blue">
          {title}
        </div>
        {subtitle ? (
          <div className="mt-0.5 flex min-w-0 items-center gap-x-2 truncate text-[11px] text-heritage-grey">
            {subtitle}
          </div>
        ) : null}
      </div>
      {actions ? (
        <div className="flex min-w-0 flex-shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      ) : null}
    </header>
  )
}

export default PageHeader
